/**
 * @module lib/election/riding-cards
 */
import {
  regions,
  getRidingByID,
  getKeyRidings,
  getRidingsByRegion,
  getRidingsByProvince,
  getIncumbentCandidate,
  getLeadingCandidate,
  getElectedCandidate,
  getVotesForAllParties,
  getVotePercentagesForAllParties,
  getTotalCountedVotesInRiding,
  getCandidateInfo,
  sortRidingsByName,
  sortCandidatesByParty
} from "lib/election/helpers";

import { getFetchedRidingDataArray } from "lib/election/service";
import initRidingCardNav from "lib/election/riding-card-nav";
import ee from "lib/eventemitter";
import { registerDialog, unregisterDialog, getDialogById, dialogs } from "ui/dialog-manager";
import ridingDialogTemplate from "ui/templates/election/riding-dialog";
import ridingCardTemplate from "ui/templates/election/riding-card";
import ridingCandidatesTableTemplate from "ui/templates/election/riding-candidates-table";
import keyRidingTemplate from "ui/templates/election/key-riding";
import { errorStateHideFeature, errorStateShowFeature } from "lib/election/error-state";
import { getById, qs, getProp } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[ELECTION-RIDING-CARDS]";

/**
 * A mapping of province IDs to SVG image directories, in Panther
 */
const provinceImageDirs = {
  1: "newfoundland",
  2: "pei",
  3: "nova-scotia",
  4: "new-brunswick",
  5: "quebec",
  6: "ontario",
  7: "manitoba",
  8: "saskatchewan",
  9: "alberta",
  10: "bc",
  11: "yukon",
  12: "nwt",
  13: "nunavut"
};

/**
 * Initialize the riding cards component
 */
export default function init() {
  logger.info(`${debugTag} init`);

  const container = getById("js-arc-election-riding-cards-container");
  const tabPanels = getById("js-election-riding-cards-tabs");
  if (!container || !tabPanels) {
    return;
  }

  ee.on("election:leading-data-failed", () => {
    errorStateHideFeature(container);
  });

  // We only need to initialize the tabs once
  ee.once("election:riding-data", () => {
    logger.info(`${debugTag} once election:riding-data`);
    initRidingCardNav(container);
  });

  // This event should be emitted every 30 seconds, when the data changes
  ee.on("election:riding-data", (ridingDataObject, ridingDataArray) => {
    logger.info(`${debugTag} on election:riding-data`);

    if (!ridingDataArray || !ridingDataArray.length) {
      logger.info(`${debugTag} no riding data`);
      return;
    }
    errorStateShowFeature(container);
    // Panels
    const keyRidings = getKeyRidings(ridingDataArray);
    populateKeyRidingsPanel(regions, keyRidings);
    populateRegionalRidingsPanels(regions, ridingDataArray);

    // Modal dialog
    const { dialog, ridingID } = setupRidingDialog("js-modal-dialog-election-riding");

    if (ridingID) {
      // Repopulate the riding card modal with the same riding that it
      // previously contained, if it was already open when fresh data arrived
      populateRidingDialog(dialog, ridingID);
    }

    initRidingDialogClickHandlers(tabPanels, dialog, riding => {
      logger.info(`${debugTag} analytics callback: ${riding.RNE}`);

      ee.trigger("analytics.trackGTMEvent", [{
        feature: "Elections 2021",
        type: "button",
        label: `Riding View: ${riding.RNE}`,
        action: "Open riding view (from list)"
      }, "clickEvent"]);
    });
  });
}

/**
 * Populate the key ridings panel with riding cards
 * @param {Object} regions - An object describing all of the regions and the provinces that belong to them
 * @param {Array<Object>} ridings - An array of riding objects
 */
function populateKeyRidingsPanel(regions, ridings) {
  const panel = qs(".arc-election-riding-cards-tabs__panel--key");
  if (!panel) {
    return;
  }
  Object.keys(regions).forEach(regionID => {
    const container = qs(`.js-arc-election-riding-cards--${regionID}`, panel);
    const cardContainer = qs(".arc-election-riding-cards__group", container);
    const ridingsByRegion = getRidingsByRegion(ridings, regionID);

    if (ridingsByRegion.length) {
      populateRidingCards(cardContainer, sortRidingsByName(ridingsByRegion));
    } else {
      container.classList.add("u-hidden");
    }
  });
}

/**
 * Populate all of the non-key ridings panels with riding cards
 * @param {Object} regions - An object describing all of the regions and the provinces that belong to them
 * @param {Array<Object>} ridings - An array of riding objects
 */
function populateRegionalRidingsPanels(regions, ridings) {
  Object.keys(regions).forEach(regionID => {
    const panel = qs(`.arc-election-riding-cards-tabs__panel--${regionID}`);
    if (!panel) {
      return;
    }
    const provinces = regions[regionID].provinces;

    Object.keys(provinces).forEach(key => {
      const province = provinces[key];
      const container = qs(`.js-arc-election-riding-cards--${key}`, panel);
      const cardContainer = qs(".arc-election-riding-cards__group", container);
      const ridingsByProvince = getRidingsByProvince(ridings, province.id);

      if (ridingsByProvince.length) {
        populateRidingCards(cardContainer, sortRidingsByName(ridingsByProvince));
      } else {
        container.classList.add("u-hidden");
      }
    });
  });
}

/**
 * Populate the given container with a collection of rendered riding cards
 * @param {HTMLElement} container - An element to inject the riding card markup into
 * @param {Array<Object>} ridings - An array of ridings objects
 */
function populateRidingCards(container, ridings) {
  if (!container || !ridings.length) {
    return;
  }
  const markup = [];
  ridings.forEach(riding => {
    const cardData = getRidingCardData(riding);
    const cardMarkup = ridingCardTemplate(cardData);
    markup.push(cardMarkup);
  });
  container.innerHTML = markup.join("");
}

/**
 * Construct the data object that will be passed into the riding card template
 * @param {Object} riding - A single riding object
 * @param {Boolean} [expanded=false] - Whether or not the card should appear in its "expanded" view
 * @returns {Object} An object containing formatted riding card data to pass to the template
 */
export function getRidingCardData(riding, expanded = false) {
  const incumbentCandidate = getIncumbentCandidate(riding.Cand);
  const leadingCandidate = getLeadingCandidate(riding.Cand);
  const electedCandidate = getElectedCandidate(riding.Cand);
  const pollStatus = getPollStatus(riding);

  // Determine which candidate's data to display
  let candidate = null;
  if (expanded) {
    switch (pollStatus) {
      case "":
      case "partial":
        candidate = leadingCandidate;
        break;
      case "complete":
        candidate = electedCandidate;
        break;
    }
  } else {
    switch (pollStatus) {
      case "":
        candidate = incumbentCandidate;
        break;
      case "partial":
        candidate = leadingCandidate;
        break;
      case "complete":
        candidate = electedCandidate;
        break;
    }
  }

  // Collect some information about the candidate
  const numberOfCountedVotes = getTotalCountedVotesInRiding(riding.Cand);
  const candidateInfo = getCandidateInfo(candidate);
  const percentageOfVotesWon = Math.round(candidateInfo.votes / numberOfCountedVotes * 100);
  candidateInfo.percentageOfVotesWon = Number.isNaN(percentageOfVotesWon) ? 0 : percentageOfVotesWon;

  // Options to pass to the riding card template
  return {
    imgDir: provinceImageDirs[riding.RegionID],
    id: riding.id,
    key: riding.keyRiding,
    name: riding.RNE,
    expanded,
    url: "#",
    totalVoters: riding.TotalVoters,
    totalPolls: riding.TotalPolls,
    reportedPolls: riding.PollsRep,
    pollStatus,
    votesByParty: expanded ? getVotePercentagesForAllParties(riding) : null,
    candidate: candidateInfo,
    candidateIsIncumbent: candidate === incumbentCandidate
  };
}

/**
 * Get the status of a given riding's polls
 * @param {Object} riding - A single riding object
 * @returns {String} The riding's poll status
 */
function getPollStatus(riding) {
  const electedCandidate = getElectedCandidate(riding.Cand);
  let pollStatus = "";

  if (electedCandidate) {
    pollStatus = "complete";
  } else if (riding.PollsRep > 0) {
    pollStatus = "partial";
  }
  return pollStatus;
}

/**
 * Destroy then reinitialize the modal dialog
 * @param {String} id - The modal dialog ID
 * @param {Object} [callbacks=null] - A collection of callback functions to pass to the dialog manager
 * @param {Object} [options={}] - Options to pass to the dialog manager
 * @returns {Object} Object containing the modal dialog instance and the ID of the riding card that previously existed inside the dialog (if applicable)
 */
export function setupRidingDialog(id, callbacks = null, options = {}) {
  const dialogContainerID = `${id}-container`;
  const dialogContainer = getById(dialogContainerID);
  const dialogID = id;

  // The following variables will all be null unless the dialog already exists
  const dialog = getDialogById(dialogID);
  const dialogRoot = dialog ? dialog.getRootElement() : null;
  const ridingCard = dialogRoot ? qs(".c-election-riding-card", dialogRoot) : null;
  const ridingID = ridingCard ? getProp(ridingCard, "dataset.ridingId", null) : null;

  if (dialog) {
    logger.info(`${debugTag} unregisterDialog: #${dialogID}`);
    unregisterDialog(dialogID);
  }
  if (!dialogContainer) {
    logger.info(`${debugTag} no dialog container: #${dialogContainerID}`);
    return;
  }
  if (dialogRoot && !dialogRoot.hidden) {
    logger.info(`${debugTag} dialog is already open`, dialogRoot);
    dialogContainer.innerHTML = ridingDialogTemplate(dialogID, false);
  } else {
    logger.info(`${debugTag} dialog is closed`, dialogRoot);
    dialogContainer.innerHTML = ridingDialogTemplate(dialogID);
  }
  logger.info(`${debugTag} registerDialog: #${dialogID}`);

  const newDialog = registerDialog(dialogID, callbacks, options);

  logger.info(`${debugTag} dialogs`, dialogs);

  return {
    dialog: newDialog,
    ridingID: ridingID ? Number(ridingID) : null
  };
}

/**
 * Initialize the riding modal dialogs
 * @param {HTMLElement} container - The riding card container
 * @param {Object} dialog - A modal dialog instance
 * @param {Function} [analyticsCallback=null] - A callback function to execute whenever the dialog opens
 */
export function initRidingDialogClickHandlers(container, dialog, analyticsCallback = null) {
  container.addEventListener("click", event => {
    event.preventDefault();
    const card = event.target.closest(".c-election-riding-card");
    if (card) {
      const ridingID = Number(card.dataset.ridingId);
      populateRidingDialog(dialog, ridingID);

      // Analytics
      if (analyticsCallback) {
        const ridings = getFetchedRidingDataArray();
        const riding = getRidingByID(ridings, ridingID);
        if (riding) {
          analyticsCallback(riding);
        }
      }
    }
  });
}

/**
 * Populate the riding modal dialog
 * @param {Object} dialog - Modal dialog instance
 * @param {Number} ridingID - The ID of a particular riding
 */
export function populateRidingDialog(dialog, ridingID) {
  logger.info(`${debugTag} populateRidingDialog: ${ridingID}`);

  const ridings = getFetchedRidingDataArray();
  const riding = getRidingByID(ridings, ridingID);
  if (!riding) {
    logger.info(`${debugTag} Riding ${ridingID} not found`);
    return;
  }
  // Expanded riding card
  const cardData = getRidingCardData(riding, true);
  const cardMarkup = ridingCardTemplate(cardData);

  const sortedCandidates = sortCandidatesByParty(riding.Cand);
  logger.info(`${debugTag} Candidates`, riding.Cand.map(c => c.PE));
  logger.info(`${debugTag} Sorted`, sortedCandidates.map(c => c.PE));

  // Collect some information about the candidates in the riding
  const votesForAllParties = getVotesForAllParties(sortedCandidates);
  const candidatesInfo = votesForAllParties.slice(0, 5).map(c => {
    const numberOfCountedVotes = getTotalCountedVotesInRiding(riding.Cand);
    const percentageOfVotesWon = Math.round(c.votes / numberOfCountedVotes * 100);
    c.percentageOfVotesWon = Number.isNaN(percentageOfVotesWon) ? 0 : percentageOfVotesWon;
    return c;
  });

  // Riding candidates table
  const tableData = {
    numberOfParties: votesForAllParties.length,
    candidates: candidatesInfo,
    incumbentCandidate: getCandidateInfo(getIncumbentCandidate(riding.Cand)),
    leadingCandidate: getCandidateInfo(getLeadingCandidate(riding.Cand)),
    electedCandidate: getCandidateInfo(getElectedCandidate(riding.Cand))
  };
  const tableMarkup = ridingCandidatesTableTemplate(tableData);

  // Key riding card
  let keyRidingMarkup = "";

  if (getProp(riding, "keyRidingData.description", null)) {
    keyRidingMarkup = `
      <div class="arc-election-riding-modal__key-riding">
        ${keyRidingTemplate(riding.keyRidingData)}
      </div>
    `;
  }
  // Dialog contents
  const content = `
    <div class="arc-election-riding-modal">
      <div class="arc-election-riding-modal__main">
        <div class="arc-election-riding-modal__card">${cardMarkup}</div>
        <div class="arc-election-riding-modal__table">${tableMarkup}</div>
      </div>
      ${keyRidingMarkup}
    </div>
  `;
  dialog.setLabel(`Riding: ${cardData.name}`);
  dialog.inject(content);
}
